import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

/**
 * OrderItem Service
 * Provides batching & caching options to help performance of the lookups required by the OrderItemList and OrderItemListItem components
 */
export default class OrderItemService {
  constructor(httpClient) {
    this.httpClient = httpClient

    this.batchList = []
    this.batchWaitTime = 250
    this.batchMaxWaitTime = 1000
    this.batchDebounceInfo = {}

    this.cache = {}
    this.cacheExpiry = 60000 * 5    // 5 minutes
  }

  // Stock lookup
  getStock(id, options = {}) {
    if(options.batchRequest) {
      return this._addBatchRequest({
        url: '/inventory/stock/lookupbatch',
        property: 'lookupList',
        id: id,
        data: { id: id }
      })
    }

    return this.httpClient.get(`/inventory/stock/${id}`)
  }

  // Stock / Contact Query
  getContactStockLookup(lookupFilter, options = {}) {
    let lookupId = uuidv4()
    var params = {
      id: lookupId,
      priceType: lookupFilter.priceType,
      stockId: lookupFilter.stockId,
      contactId: lookupFilter.contactId || 0,
      salesRateId: lookupFilter.defaultSalesRateId || 0,
      stockPriceGroupId: lookupFilter.stockPriceGroupId || 0,
      thickness: lookupFilter.thickness || 0      
    }

    if(options.batchRequest) {
      return this._addBatchRequest({
        url: '/orders/orderitems/lookupbatch',
        property: 'lookupList',
        key: JSON.stringify(_.omit(params, 'id')),
        id: lookupId,
        data: params
      })
    }
      
    return this.httpClient.get(`/orders/orderitems/lookup?${$.param(params)}`)
  }

  // Stock / Production Query
  getProductionStockLookup(lookupFilter, options = {}) {
    let lookupId = uuidv4()
    var params = {
      id: lookupId,
      stockId: lookupFilter.stockId,
      stockPriceGroupId: lookupFilter.stockPriceGroupId || 0,
      thickness: lookupFilter.thickness || 0      
    }

    if(options.batchRequest) {
      return this._addBatchRequest({
        url: '/production/stock/lookupbatch',
        property: 'lookupList',
        key: JSON.stringify(_.omit(params, 'id')),
        id: lookupId,
        data: params
      })
    }
      
    return this.httpClient.get(`/production/stock/lookup?${$.param(params)}`)
  }


  /**
   * Finish the batch mode
   * Runs the requests as a batch query & resolves promises added to the list
   */
  _finishBatchMode(url) {
    // Collate same request URL into a single request
    let requestList = []
    this.batchList.filter(batchInfo => batchInfo.url == url).forEach(batchInfo => {
      let curRequest = requestList.find(request => {
        return request.url == batchInfo.url
      })

      if(curRequest) {
        curRequest.dataList = curRequest.dataList.concat(batchInfo.dataList)
        curRequest.promiseList = curRequest.promiseList.concat(batchInfo.promiseList)
      } else {
        requestList.push({
          url: batchInfo.url,
          property: batchInfo.property,
          dataList: batchInfo.dataList,
          promiseList: batchInfo.promiseList
        })
      }
    })

    this.batchList = this.batchList.filter(batchInfo => batchInfo.url != url)

    // Run the requests
    requestList.forEach(request => {
      let batchData = request.dataList

      this.httpClient.post(request.url, { [request.property]: batchData })
      .then(response => {
        if(_.isArray(response.data)) {
          response.data.forEach(lookupRes => {

            request.promiseList.filter(promiseInfo => {
              return promiseInfo.id == lookupRes.id
            }).forEach(promiseInfo => {

              this.cache[promiseInfo.cacheKey] = {
                data: lookupRes,
                time: new Date().getTime()
              }

              if(promiseInfo.resolve)
                promiseInfo.resolve({ data: lookupRes })
            })
          })
        } else {
          throw 'Invalid data from server'
        }
      })
      .catch(err => {
        console.error(err)

        // Reject all promises in request
        request.promiseList.forEach(promiseInfo => {
          if(promiseInfo.reject)
            promiseInfo.reject(err)
        })
      })
    })
  }


  /**
   * Add a request to the batch
   * Returns a promise, resolved with request response when batch is finished
   */
  _addBatchRequest(requestData) {
    return new Promise((resolve, reject) => {
      try {
        let curTimeMs = new Date().getTime()
        let requestKey = requestData.key || JSON.stringify(requestData.data)
        let cacheKey = `${requestKey}:${requestData.url}`

        // If in cache, just return that straight away
        let cacheData = this.cache[cacheKey]
        if(cacheData) {
          if(curTimeMs - cacheData.time < this.cacheExpiry)
            return resolve({ data: cacheData.data })
        }

        // If the request data matches up exactly with another request, combine it with an existing batch request
        let existingBatch = this.batchList.find(curBatch => {
          return (curBatch.url == requestData.url && curBatch.key == requestKey)
        })
        if(existingBatch) {
          existingBatch.promiseList.push({
            id: existingBatch.id,
            cacheKey: cacheKey,
            resolve: resolve,
            reject: reject
          })

        } else {
          this.batchList.push({
            id: requestData.id,
            url: requestData.url,
            property: requestData.property,
            key: requestKey,
            dataList: [requestData.data],
            promiseList: [{
              id: requestData.id,
              cacheKey: cacheKey,
              resolve: resolve,
              reject: reject
            }]
          })
        }

        // Debounce the request to wait to pool up the requests
        let debounceInfo = this.batchDebounceInfo[requestData.url]
        if(!debounceInfo) {
          debounceInfo = {
            batchDelayTimer: null,
            batchStartTime: curTimeMs
          }
          this.batchDebounceInfo[requestData.url] = debounceInfo
        }

        if(debounceInfo.batchDelayTimer) {
          clearTimeout(debounceInfo.batchDelayTimer)
          debounceInfo.batchDelayTimer = null
        }

        // If waiting too long, force it now
        if(curTimeMs - debounceInfo.batchStartTime >= this.batchMaxWaitTime) {
          this.batchDebounceInfo[requestData.url] = null
          this._finishBatchMode(requestData.url)
        } else {
          // Wait a little longer to pool up requests
          debounceInfo.batchDelayTimer = setTimeout(() => {
            this.batchDebounceInfo[requestData.url] = null
            this._finishBatchMode(requestData.url)
          }, this.batchWaitTime)
        }

      } catch(err) {
        console.error(err)
        return reject(err)
      }
    })
  }



}